<script lang="ts" setup>
  import { type PropType, ref, onMounted, onBeforeUnmount } from 'vue';
  import { useHttpClient, useRoute } from '@othercommerce/foundation';
  import type { Complaint, ComplaintMessage } from '@storefront/resources';

  const props = defineProps({
    message: { type: Object as PropType<ComplaintMessage>, required: true },
    complaint: { type: Object as PropType<Complaint>, required: true },
  });

  const route = useRoute();
  const http = useHttpClient();
  const messageData = ref(props.message);

  const htmlMessageRef = ref<HTMLElement | null>(null);
  let observer: IntersectionObserver | null = null;
  let visibilityTimer: number | null = null;

  async function markAsRead() {
    try {
      const response = await http.patch(
        route('web.complaint.message.api', {
          complaint: props.complaint,
          message: props.message,
        }),
      );

      messageData.value = response ?? messageData.value;

    } catch (error) {
      console.error('Failed to update message:', error);
    }
  }

  function startVisibilityTimer() {
    if (visibilityTimer !== null || !messageData.value.customerUnread) return;

    visibilityTimer = window.setTimeout(() => {
      markAsRead();

      visibilityTimer = null;
    }, 5000);
  }

  function clearVisibilityTimer() {
    if (visibilityTimer !== null) {
      clearTimeout(visibilityTimer);
      visibilityTimer = null;
    }
  }

  onMounted(() => {
    observer = new IntersectionObserver((entries) => {
      const entry = entries[0];

      if (entry?.isIntersecting) {
        startVisibilityTimer();
      } else {
        clearVisibilityTimer();
      }
    }, {
      threshold: 1,
    });

    if (htmlMessageRef.value) {
      observer.observe(htmlMessageRef.value);
    }
  });

  onBeforeUnmount(() => {
    if (observer && htmlMessageRef.value) observer.unobserve(htmlMessageRef.value);
    clearVisibilityTimer();
  });
</script>
<template>
  <article ref="htmlMessageRef" class="message">
    <div class="message__wrapper">
      <div class="message__avatar">
        <SfAvatar :avatar="messageData.administratorAvatar" :name="messageData.name || $t('SHOP.COMMON.LABEL.CHAT_USER_UNKNOWN')" />
      </div>
      <div class="message__container">
        <header class="message__header">
          <div class="message__id">
            {{ '#' + messageData.id }}
            <span v-if="messageData.customerUnread" class="message__new"> - {{ $t('SHOP.COMMON.LABEL.CHAT_MESSAGE_NEW') }}</span>
          </div>
          <h3 class="message__author">
            {{ messageData.name || $t('SHOP.COMMON.LABEL.USER_DELETED') }}
          </h3>
          <p class="message__date">
            {{ $t('SHOP.COMMON.LABEL.CHAT_MESSAGE_SENT_AT') }}
            <SfDateTime :value="messageData.createdAt" time />
          </p>
        </header>
        <div class="message__content" v-html="messageData.message" />
        <div v-if="messageData.files && messageData.files?.length > 0">
          <ul class="message__files">
            <li v-for="file in messageData.files">
              <a :href="file.raw || $route('public.media.file.download', { filename: file.filename })" target="_blank">
                <span class="icon">
                  <SfIconDocument aria-hidden="true" />
                </span>
                <span class="name">
                  {{ file.displayName || file.original || file.filename }}
                </span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </article>
</template>
<style lang="scss" scoped>
  .message {
    background: var(--secondary-background);
    border-radius: var(--border-radius);
    display: flex;
    flex-direction: column;
    padding: 1rem;
    gap: 0.5rem;

    &__wrapper {
      display: flex;
      gap: 1rem;
      align-items: flex-start;
      width: 100%;
    }

    &__avatar {
      flex: 0 0;
      width: 3rem;

      --sf-avatar-border: var(--secondary-border-hover);
      --sf-avatar-background: var(--secondary-background-hover);
      --sf-avatar-color: var(--secondary-foreground-hover);
    }

    &__container {
      width: 100%;
    }

    &__new {
      font-size: 0.625rem;
      color: var(--secondary-color);
      font-weight: var(--font-weight-bold);
    }

    &__header {
      position: relative;
      border-bottom: 1px solid var(--secondary-border-hover);
      padding-bottom: 0.25rem;
    }

    &__id {
      top: 0;
      right: 0;
      position: absolute;
      font-family: var(--font-family-mono);
      font-size: 0.625rem;
      color: var(--secondary-color);
    }

    &__content {
      font-size: 0.875rem;
    }

    &__files {
      line-height: 1.5rem;
      list-style: none;
      padding: 0.75rem 0 0 0;
      margin: 0;

      a {
        display: flex;
        align-items: flex-start;
        line-height: 1rem;
        font-size: 0.875rem;
        gap: 0.5rem;

        svg {
          display: inline-block;
          font-size: 1rem;
        }
      }
    }

    &__author {
      font-weight: var(--font-weight-bold);
      font-size: 1rem;
    }

    &__date {
      color: var(--secondary-color);
      font-size: 0.625rem;
    }
  }
</style>
